'use client';

import { c as _c } from "react/compiler-runtime";
import AddCommentField from '@/app/[locale]/(worlds)/[roomId]/components/AddCommentField';
import Comment from '@/app/[locale]/(worlds)/[roomId]/components/Comment';
import SortPopoverButton from '@/app/[locale]/(worlds)/[roomId]/components/SortPopoverButton';
import { LoadingDots } from '@/components/LoadingDots';
import { Separator } from '@/components/ui/separator';
import { ReportReason } from '@/constants/comment';
import { useCheckEnterRoomContext } from '@/context/CheckEnterRoomContext';
import { useConfigsContext } from '@/context/ConfigsContext';
import { useVideoCommentsPanelContext } from '@/context/VideoCommentsPanelContext';
import { useFullscreenStatus } from '@/hooks/useFullscreenStatus';
import { cn } from '@/lib/utils';
import { deleteCommentAction, postCommentAction, reportCommentAction, toggleLikeAction } from '@/server/lib/comment';
import { getComments, getCommentsCount } from '@/services/cms';
import { Comment as CommentType, CommentsData, CommentsSortOptions, SortOptionValue } from '@/types/comments';
import { useTranslations } from 'next-intl';
import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import useSWR from 'swr';
import useSWRInfinite from 'swr/infinite';
import { useIntersectionObserver } from 'usehooks-ts';
const COMMENT_PAGE_LIMIT = 50;
export const COMMENTS_SORT_OPTIONS: CommentsSortOptions = {
  top: {
    label: 'top-comments',
    value: 'top'
  },
  latest: {
    label: 'latest',
    value: 'latest'
  }
};
function sortComments(comments: CommentType[], value: SortOptionValue) {
  const copied = [...comments];
  if (value === 'top') {
    copied.sort((a, b) => b.like_count - a.like_count);
  } else if (value === 'latest') {
    copied.sort((a, b) => Date.parse(b.date_created) - Date.parse(a.date_created));
  }
  return copied;
}
type CommentsPanelProps = {
  variant?: 'inline' | 'drawer';
};
function CommentsPanel(t0) {
  const $ = _c(114);
  const {
    variant: t1
  } = t0;
  const variant = t1 === undefined ? "inline" : t1;
  const t = useTranslations();
  const {
    isCommentPanelOpen,
    panelHostEl
  } = useVideoCommentsPanelContext();
  const {
    isFullscreen
  } = useFullscreenStatus();
  const isDrawerMode = variant === "drawer";
  const {
    roomData,
    isOwnerViversePro
  } = useCheckEnterRoomContext();
  const {
    store
  } = useConfigsContext();
  const isCinematicMode = isOwnerViversePro;
  const [sortOrder, setSortOrder] = useState(COMMENTS_SORT_OPTIONS.top.value);
  const [isSorting, setIsSorting] = useState(false);
  const [commentsCount, setCommentsCount] = useState(0);
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [];
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const [tempComments, setTempComments] = useState(t2);
  const [isAddingComment, setIsAddingComment] = useState(false);
  let t3;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      threshold: 0.1,
      rootMargin: "100px"
    };
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const {
    ref: lastCommentRef,
    isIntersecting,
    entry
  } = useIntersectionObserver(t3);
  let t4;
  if ($[2] !== roomData.id) {
    t4 = () => getCommentsCount({
      roomId: roomData.id
    });
    $[2] = roomData.id;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      revalidateOnFocus: false
    };
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  const {
    data: commentsCountData,
    mutate: mutateCommentsCount
  } = useSWR("comments/count", t4, t5);
  let t6;
  if ($[5] !== roomData.id) {
    t6 = (pageIndex, previousPageData) => {
      if (!roomData?.id) {
        return null;
      }
      if (pageIndex === 0 || !previousPageData) {
        return `comments?limit=${COMMENT_PAGE_LIMIT}&room_id=${roomData.id}&cursor=`;
      }
      if (!previousPageData || !previousPageData.pages?.next) {
        return null;
      }
      return previousPageData.pages.next;
    };
    $[5] = roomData.id;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  const getKey = t6;
  let t7;
  if ($[7] !== roomData.id || $[8] !== store?.credentials?.htcToken) {
    t7 = key => {
      const urlParams = new URLSearchParams(key.split("?")[1]);
      const cursor = urlParams.get("cursor") || "";
      return getComments({
        roomId: roomData.id,
        cursor,
        limit: COMMENT_PAGE_LIMIT,
        htcAuthKey: store?.credentials?.htcToken
      });
    };
    $[7] = roomData.id;
    $[8] = store?.credentials?.htcToken;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      revalidateFirstPage: false
    };
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  const {
    data,
    isLoading,
    isValidating,
    setSize,
    mutate
  } = useSWRInfinite(getKey, t7, t8);
  let t9;
  if ($[11] !== data) {
    t9 = data && data?.length > 0 ? data.flatMap(_temp) : [];
    $[11] = data;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  const baseComments = t9;
  let t10;
  let t11;
  if ($[13] !== baseComments) {
    t10 = () => {
      if (baseComments.length === 0) {
        return;
      }
      const baseIds = new Set(baseComments.map(_temp2));
      setTempComments(prev => {
        const hasOverlap = prev.some(comment_0 => baseIds.has(comment_0.id));
        if (hasOverlap) {
          return prev.filter(comment_1 => !baseIds.has(comment_1.id));
        }
        return prev;
      });
    };
    t11 = [baseComments];
    $[13] = baseComments;
    $[14] = t10;
    $[15] = t11;
  } else {
    t10 = $[14];
    t11 = $[15];
  }
  useEffect(t10, t11);
  let t12;
  if ($[16] !== tempComments) {
    t12 = new Set(tempComments.map(_temp3));
    $[16] = tempComments;
    $[17] = t12;
  } else {
    t12 = $[17];
  }
  const tempIds = t12;
  let t13;
  if ($[18] !== baseComments || $[19] !== sortOrder || $[20] !== tempComments || $[21] !== tempIds) {
    let t14;
    if ($[23] !== tempIds) {
      t14 = comment_3 => !tempIds.has(comment_3.id);
      $[23] = tempIds;
      $[24] = t14;
    } else {
      t14 = $[24];
    }
    const filteredBase = baseComments.filter(t14);
    t13 = [...tempComments, ...sortComments([...filteredBase], sortOrder)];
    $[18] = baseComments;
    $[19] = sortOrder;
    $[20] = tempComments;
    $[21] = tempIds;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  const renderComments = t13;
  const canLoadMore = !!(!isLoading && !isValidating && data && data.length > 0 && data[data.length - 1]?.pages?.next);
  let t14;
  if ($[25] !== roomData.id || $[26] !== store?.credentials?.htcToken) {
    t14 = async value => {
      const htcToken = store?.credentials?.htcToken;
      if (!value.trim() || !htcToken || !roomData?.id) {
        return;
      }
      ;
      try {
        setIsAddingComment(true);
        const created = await postCommentAction({
          roomId: roomData.id,
          content: value,
          htcAuthKey: htcToken
        });
        setTempComments(prev_0 => [created, ...prev_0]);
        setCommentsCount(_temp4);
      } catch (t15) {
        const err = t15;
        console.error("Failed to add comment:", err);
      }
      setIsAddingComment(false);
    };
    $[25] = roomData.id;
    $[26] = store?.credentials?.htcToken;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  const addComment = t14;
  let t15;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = (commentId, delta) => {
      setTempComments(prev_2 => prev_2.map(comment_4 => comment_4.id === commentId ? {
        ...comment_4,
        reply_count: Math.max(0, (comment_4.reply_count || 0) + delta)
      } : comment_4));
      setCommentsCount(prev_3 => Math.max(0, prev_3 + delta));
    };
    $[28] = t15;
  } else {
    t15 = $[28];
  }
  const updateTempReplyCount = t15;
  let t16;
  if ($[29] !== mutate) {
    t16 = (commentId_0, delta_0) => {
      mutate(pages => pages?.map(page_0 => ({
        ...page_0,
        results: (page_0?.results || []).map(comment_5 => comment_5.id === commentId_0 ? {
          ...comment_5,
          reply_count: Math.max(0, (comment_5.reply_count || 0) + delta_0)
        } : comment_5)
      })), false);
      setCommentsCount(prev_4 => Math.max(0, prev_4 + delta_0));
    };
    $[29] = mutate;
    $[30] = t16;
  } else {
    t16 = $[30];
  }
  const updateReplyMutate = t16;
  let t17;
  if ($[31] !== tempComments || $[32] !== updateReplyMutate) {
    t17 = (commentId_1, delta_1) => {
      const existedInTemp = tempComments.some(comment_6 => comment_6.id === commentId_1);
      if (existedInTemp) {
        updateTempReplyCount(commentId_1, delta_1);
      } else {
        updateReplyMutate(commentId_1, delta_1);
      }
    };
    $[31] = tempComments;
    $[32] = updateReplyMutate;
    $[33] = t17;
  } else {
    t17 = $[33];
  }
  const updateReplyCount = t17;
  let t18;
  if ($[34] !== store?.credentials?.htcToken) {
    t18 = (commentId_2, isLiked) => {
      const htcToken_0 = store?.credentials?.htcToken;
      if (!htcToken_0) {
        return;
      }
      toggleLikeAction({
        commentId: commentId_2,
        htcAuthKey: htcToken_0,
        isLiked
      });
    };
    $[34] = store?.credentials?.htcToken;
    $[35] = t18;
  } else {
    t18 = $[35];
  }
  const updateLikeCount = t18;
  let t19;
  if ($[36] !== data || $[37] !== mutate || $[38] !== store?.credentials?.htcToken) {
    t19 = async (commentId_3, reason, detail) => {
      const htcToken_1 = store?.credentials?.htcToken;
      if (!htcToken_1) {
        return;
      }
      const cache = data;
      ;
      try {
        mutate(pages_0 => pages_0?.map(page_1 => ({
          ...page_1,
          results: page_1?.results?.map(comment_7 => comment_7.id === commentId_3 ? {
            ...comment_7,
            is_reported: true
          } : comment_7)
        })), false);
        await reportCommentAction({
          commentId: commentId_3,
          htcAuthKey: htcToken_1,
          reason,
          detail
        });
      } catch (t20) {
        const err_0 = t20;
        mutate(cache, false);
        console.error("Failed to report comment:", err_0);
      }
    };
    $[36] = data;
    $[37] = mutate;
    $[38] = store?.credentials?.htcToken;
    $[39] = t19;
  } else {
    t19 = $[39];
  }
  const reportCommentMutate = t19;
  let t20;
  if ($[40] !== store?.credentials?.htcToken || $[41] !== tempComments) {
    t20 = async commentId_4 => {
      const htcToken_2 = store?.credentials?.htcToken;
      if (!htcToken_2) {
        return;
      }
      const cache_0 = tempComments;
      let replyCount = 0;
      ;
      try {
        setTempComments(prev_6 => prev_6.filter(comment_8 => {
          if (comment_8.id === commentId_4) {
            replyCount = comment_8.reply_count || 0;
          }
          return comment_8.id !== commentId_4;
        }));
        setCommentsCount(prev_7 => Math.max(0, prev_7 - 1 - replyCount));
        await deleteCommentAction({
          commentId: commentId_4,
          htcAuthKey: htcToken_2
        });
      } catch (t21) {
        const err_1 = t21;
        setTempComments(cache_0);
        setCommentsCount(prev_5 => prev_5 + 1 + replyCount);
        console.error("Failed to delete temp comment:", err_1);
      }
    };
    $[40] = store?.credentials?.htcToken;
    $[41] = tempComments;
    $[42] = t20;
  } else {
    t20 = $[42];
  }
  const removeTempComment = t20;
  let t21;
  if ($[43] !== data || $[44] !== mutate || $[45] !== store?.credentials?.htcToken) {
    t21 = async commentId_5 => {
      const htcToken_3 = store?.credentials?.htcToken;
      if (!htcToken_3) {
        return;
      }
      const cache_1 = data;
      let replyCount_0 = 0;
      ;
      try {
        mutate(pages_1 => pages_1?.map(page_2 => ({
          ...page_2,
          results: (page_2?.results || []).filter(comment_9 => {
            if (comment_9.id === commentId_5) {
              replyCount_0 = comment_9.reply_count || 0;
            }
            return comment_9.id !== commentId_5;
          })
        })), false);
        setCommentsCount(prev_9 => Math.max(0, prev_9 - 1 - replyCount_0));
        await deleteCommentAction({
          commentId: commentId_5,
          htcAuthKey: htcToken_3
        });
      } catch (t22) {
        const err_2 = t22;
        mutate(cache_1, false);
        setCommentsCount(prev_8 => prev_8 + 1 + replyCount_0);
        console.error("Failed to delete comment:", err_2);
      }
    };
    $[43] = data;
    $[44] = mutate;
    $[45] = store?.credentials?.htcToken;
    $[46] = t21;
  } else {
    t21 = $[46];
  }
  const removeCommentMutate = t21;
  let t22;
  if ($[47] !== removeCommentMutate || $[48] !== removeTempComment || $[49] !== tempComments) {
    t22 = commentId_6 => {
      const existedInTemp_0 = tempComments.some(comment_10 => comment_10.id === commentId_6);
      if (existedInTemp_0) {
        removeTempComment(commentId_6);
      } else {
        removeCommentMutate(commentId_6);
      }
    };
    $[47] = removeCommentMutate;
    $[48] = removeTempComment;
    $[49] = tempComments;
    $[50] = t22;
  } else {
    t22 = $[50];
  }
  const removeComment = t22;
  let t23;
  if ($[51] !== mutate || $[52] !== mutateCommentsCount) {
    t23 = async sortOrder_0 => {
      setIsSorting(true);
      await mutate();
      await mutateCommentsCount();
      setSortOrder(sortOrder_0);
      setIsSorting(false);
    };
    $[51] = mutate;
    $[52] = mutateCommentsCount;
    $[53] = t23;
  } else {
    t23 = $[53];
  }
  const handleSort = t23;
  let t24;
  if ($[54] !== canLoadMore || $[55] !== isIntersecting || $[56] !== setSize) {
    t24 = () => {
      if (isIntersecting && canLoadMore) {
        setSize(_temp5);
      }
    };
    $[54] = canLoadMore;
    $[55] = isIntersecting;
    $[56] = setSize;
    $[57] = t24;
  } else {
    t24 = $[57];
  }
  let t25;
  if ($[58] !== canLoadMore || $[59] !== entry || $[60] !== isIntersecting || $[61] !== setSize) {
    t25 = [isIntersecting, canLoadMore, entry, setSize];
    $[58] = canLoadMore;
    $[59] = entry;
    $[60] = isIntersecting;
    $[61] = setSize;
    $[62] = t25;
  } else {
    t25 = $[62];
  }
  useEffect(t24, t25);
  let t26;
  let t27;
  if ($[63] !== commentsCountData) {
    t26 = () => {
      if (commentsCountData) {
        setCommentsCount(commentsCountData.pages?.total ?? 0);
      }
    };
    t27 = [commentsCountData];
    $[63] = commentsCountData;
    $[64] = t26;
    $[65] = t27;
  } else {
    t26 = $[64];
    t27 = $[65];
  }
  useEffect(t26, t27);
  const isHostedMode = isFullscreen && isCommentPanelOpen && panelHostEl !== null;
  const t28 = variant === "inline";
  const t29 = !isHostedMode && isDrawerMode;
  const t30 = !isHostedMode && !isDrawerMode && isCinematicMode;
  const t31 = !isHostedMode && !isDrawerMode && !isCinematicMode;
  let t32;
  if ($[66] !== isHostedMode || $[67] !== t28 || $[68] !== t29 || $[69] !== t30 || $[70] !== t31) {
    t32 = cn({
      "max-md:px-4": t28,
      "flex h-full w-full flex-col gap-5 overflow-hidden p-6": isHostedMode,
      "flex h-full w-full flex-col gap-5 overflow-hidden": t29,
      "mb-12 flex h-fit w-full flex-col justify-between gap-5 md:mb-20 md:gap-6 lg:col-start-1 lg:row-start-2 lg:max-w-full": t30,
      "3xl:max-w-[1440px] mb-12 flex h-fit w-full flex-col justify-between gap-5 md:mb-20 md:gap-6 lg:col-start-1 lg:row-start-2 lg:max-w-[75vw]": t31
    });
    $[66] = isHostedMode;
    $[67] = t28;
    $[68] = t29;
    $[69] = t30;
    $[70] = t31;
    $[71] = t32;
  } else {
    t32 = $[71];
  }
  let t33;
  if ($[72] !== t) {
    t33 = t("comments-panel.comments-count");
    $[72] = t;
    $[73] = t33;
  } else {
    t33 = $[73];
  }
  const t34 = `${commentsCount || ""} ${t33}`;
  let t35;
  if ($[74] !== t34) {
    t35 = <span className="header2-omni-styles">{t34}</span>;
    $[74] = t34;
    $[75] = t35;
  } else {
    t35 = $[75];
  }
  const t36 = isHostedMode ? panelHostEl : null;
  let t37;
  if ($[76] !== handleSort || $[77] !== isHostedMode || $[78] !== sortOrder || $[79] !== t36) {
    t37 = <SortPopoverButton sortOrder={sortOrder} setSortOrder={handleSort} isRailMode={isHostedMode} portalContainer={t36} />;
    $[76] = handleSort;
    $[77] = isHostedMode;
    $[78] = sortOrder;
    $[79] = t36;
    $[80] = t37;
  } else {
    t37 = $[80];
  }
  let t38;
  if ($[81] !== t35 || $[82] !== t37) {
    t38 = <div className="flex items-center justify-between">{t35}{t37}</div>;
    $[81] = t35;
    $[82] = t37;
    $[83] = t38;
  } else {
    t38 = $[83];
  }
  let t39;
  if ($[84] !== addComment) {
    t39 = <AddCommentField addComment={addComment} />;
    $[84] = addComment;
    $[85] = t39;
  } else {
    t39 = $[85];
  }
  let t40;
  if ($[86] === Symbol.for("react.memo_cache_sentinel")) {
    t40 = <Separator className="bg-vive-surface-normal" />;
    $[86] = t40;
  } else {
    t40 = $[86];
  }
  const t41 = isHostedMode || isDrawerMode ? "flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto" : "flex h-fit w-full flex-col gap-5";
  let t42;
  if ($[87] !== isAddingComment) {
    t42 = isAddingComment && <LoadingDots size="md" />;
    $[87] = isAddingComment;
    $[88] = t42;
  } else {
    t42 = $[88];
  }
  let t43;
  if ($[89] !== canLoadMore || $[90] !== isSorting || $[91] !== lastCommentRef || $[92] !== removeComment || $[93] !== renderComments || $[94] !== reportCommentMutate || $[95] !== updateLikeCount || $[96] !== updateReplyCount) {
    t43 = !isSorting && renderComments.map((comment_11, index) => {
      const isLastComment = index === renderComments.length - 1;
      return <Comment key={comment_11.id} comment={comment_11} lastCommentRef={isLastComment && canLoadMore ? lastCommentRef : undefined} removeComment={removeComment} updateCommentReplyCount={updateReplyCount} updateCommentLikeCount={updateLikeCount} reportComment={reportCommentMutate} />;
    });
    $[89] = canLoadMore;
    $[90] = isSorting;
    $[91] = lastCommentRef;
    $[92] = removeComment;
    $[93] = renderComments;
    $[94] = reportCommentMutate;
    $[95] = updateLikeCount;
    $[96] = updateReplyCount;
    $[97] = t43;
  } else {
    t43 = $[97];
  }
  let t44;
  if ($[98] !== isLoading || $[99] !== isValidating) {
    t44 = isLoading || isValidating && <LoadingDots size="md" />;
    $[98] = isLoading;
    $[99] = isValidating;
    $[100] = t44;
  } else {
    t44 = $[100];
  }
  let t45;
  if ($[101] !== t41 || $[102] !== t42 || $[103] !== t43 || $[104] !== t44) {
    t45 = <div className={t41}>{t42}{t43}{t44}</div>;
    $[101] = t41;
    $[102] = t42;
    $[103] = t43;
    $[104] = t44;
    $[105] = t45;
  } else {
    t45 = $[105];
  }
  let t46;
  if ($[106] !== t32 || $[107] !== t38 || $[108] !== t39 || $[109] !== t45) {
    t46 = <div data-section="comments" className={t32}>{t38}{t39}{t40}{t45}</div>;
    $[106] = t32;
    $[107] = t38;
    $[108] = t39;
    $[109] = t45;
    $[110] = t46;
  } else {
    t46 = $[110];
  }
  const panelContent = t46;
  if (isHostedMode && panelHostEl) {
    let t47;
    if ($[111] !== panelContent || $[112] !== panelHostEl) {
      t47 = createPortal(panelContent, panelHostEl);
      $[111] = panelContent;
      $[112] = panelHostEl;
      $[113] = t47;
    } else {
      t47 = $[113];
    }
    return t47;
  }
  if (isFullscreen) {
    return null;
  }
  return panelContent;
}
function _temp5(prev_10) {
  return prev_10 + 1;
}
function _temp4(prev_1) {
  return prev_1 + 1;
}
function _temp3(comment_2) {
  return comment_2.id;
}
function _temp2(comment) {
  return comment.id;
}
function _temp(page) {
  return page?.results || [];
}
export default CommentsPanel;